/**
 * 复制按钮组件
 *
 * 基于 Ant Design Typography.Text 的 copyable 功能封装的复制按钮组件，
 * 支持按钮和链接两种展示形式，点击后将指定文本复制到剪贴板并显示成功提示。
 *
 * @module CopyButton
 */
import React from 'react';
import {
  Typography,
  message,
  Button,
} from 'antd';
import { getIntl } from '@/utils/intl';

const intl = getIntl();

/**
 * CopyButton 组件的 Props 类型定义
 */
export type CopyButtonProps = {
  /** 按钮显示文本 */
  buttonText: any;
  /** 按钮类型：'button' 为按钮样式，其他值为链接样式 */
  buttonType?: string;
  /** 要复制到剪贴板的文本内容 */
  copyText: string;
}

/**
 * 复制按钮组件
 *
 * 提供一键复制功能，支持两种展示形式：
 * - buttonType='button': 显示为按钮样式
 * - buttonType='link': 显示为链接样式
 *
 * 复制成功后会自动显示国际化的成功提示消息。
 *
 * @param props - 组件属性
 * @returns 复制按钮组件
 *
 * @example
 * // 按钮样式
 * <CopyButton
 *   buttonText="复制订单号"
 *   copyText="ORDER123456"
 *   buttonType="button"
 * />
 *
 * @example
 * // 链接样式
 * <CopyButton
 *   buttonText="复制链接"
 *   copyText="https://example.com"
 *   buttonType="link"
 * />
 */
const CopyButton: React.FC<CopyButtonProps> = ({ buttonText, copyText, buttonType = 'button' }) => {
  return (
    <Typography.Text
      className='inline-block mr-xs'
      copyable={{
        // 自定义复制图标，根据 buttonType 渲染不同样式
        icon: [
          // 复制前的图标（未点击状态）
          buttonType == 'button' ?
            <Button key="copyBefore" disabled={!copyText}>
              {buttonText}
            </Button>
            : <a key="copyBefore">{buttonText}</a>,
          // 复制后的图标（点击后状态）
          buttonType == 'button' ? <Button key="copyAfter" disabled={!copyText}>
            {buttonText}
          </Button>
            : <a key="copyAfter">{buttonText}</a>
        ],
        // 要复制的文本内容
        text: copyText,
        // 禁用默认的 tooltip 提示
        tooltips: false,
        // 复制成功回调
        onCopy: () => {
          message.success(intl.formatMessage({ defaultMessage: '复制成功' }));
        }
      }}>
    </Typography.Text>
  );
};

// 设置默认属性
CopyButton.defaultProps = {
  buttonText: intl.formatMessage({ defaultMessage: '复制' }),
};

export default CopyButton;
